// Exact-size GPU slab allocator for streamed-SOG splats — Part B of the
// "match PlayCanvas" streaming rewrite (see STREAMING-SOG-STATUS.md and
// /Users/uldis.liepins/.claude/plans/floating-launching-blum.md).
//
// Same public API and same GPU texture layout as SplatPool (splatPool.js) —
// SplatOctree/Splat.ts only need a constructor swap, gated by ?exactPack=1.
// The ONE thing that's different: allocation is EXACT-SIZED, not rounded up
// to a power-of-two bucket. A real device screenshot showed the buddy
// allocator's rounding alone (MIN_BUCKET=512) accounted for a large gap
// between raw resident splat count (75% of budget) and actual pool
// exhaustion (poolFree: 0 largest / 0 total) — this class exists to close
// that gap directly. See splatPool.js's own header comment for the buddy
// allocator this parallels; texture creation and the texSubImage2D write
// path below are copied verbatim from there (offset-to-texel mapping is
// identical regardless of how offsets were allocated).
//
// Free space is tracked as a sorted-by-offset, always-coalesced array of
// {offset, count} intervals — first-fit allocation (walk from the start,
// take the first interval big enough). Deliberately not the more complex
// best-fit-via-size-class-buckets structure: at this app's real scale
// (thousands of leaves, not millions) a first-fit scan over a typically-
// small free list is simple, easy to verify correct, and fast enough — see
// the implementation plan's own judgment call on this. First-fit-from-the-
// low-end also has a natural self-compacting bias (allocations cluster low,
// free space accumulates high) that reduces how often fragmentation-driven
// defrag is actually needed in practice.

import * as THREE from 'three';
import { quarantineDone } from './splatPool';
import { isWebGPU, maxTextureSize, fitShDegree, setUintTextureFormat, writeTextureRowWebGPU, zeroRangeWebGPU, initPoolTexturesWebGPU, BYTES_PER_TEXEL } from './poolTextureWrite';

const QUARANTINE_TICKS = 10; // same rationale as SplatPool's — outlasts an in-flight sort round-trip

export class SplatPoolExact {
  /**
   * @param {THREE.WebGLRenderer} gl
   * @param {number} budget - fixed splat capacity for the life of this pool
   * @param {number} shDegree - 0 disables the SH texture entirely
   */
  constructor(gl, budget, shDegree = 0) {
    this.gl = gl;
    this.budget = budget;
    this.shDegree = shDegree;
    this.exact = true; // lets SplatOctree tell this apart from the buddy-allocator SplatPool

    this.isWebGPU = isWebGPU(gl);
    // Bumped on every write/free — WebGPU's GPU sort re-sorts when it changes.
    this.version = 0;
    this.width = Math.min(maxTextureSize(gl), budget);
    this.height = Math.floor((budget - 1) / this.width) + 1;
    this.capacity = this.width * this.height;

    this.centerAndScaleData = new Float32Array(this.width * this.height * 4);
    this.covAndColorData = new Uint32Array(this.width * this.height * 4);
    this.centerAndScaleTexture = new THREE.DataTexture(
      this.centerAndScaleData, this.width, this.height, THREE.RGBAFormat, THREE.FloatType
    );
    this.centerAndScaleTexture.needsUpdate = true;

    this.covAndColorTexture = new THREE.DataTexture(
      this.covAndColorData, this.width, this.height, THREE.RGBAIntegerFormat, THREE.UnsignedIntType
    );
    setUintTextureFormat(gl, this.covAndColorTexture);
    this.covAndColorTexture.needsUpdate = true;

    // SH texture height = height × rows per splat (12 for degree 3). Lower
    // the degree until it fits the GPU's max texture size (WebGPU's default
    // limit is 8192 — an 8M budget at degree 3 needs 11,724) rather than
    // creating an invalid texture, or one over the memory cap (see
    // fitShDegree); 0 = no SH.
    shDegree = fitShDegree(this.width, this.height, shDegree, maxTextureSize(gl));
    this.shDegree = shDegree;
    this.hasSH = shDegree > 0;
    this.shRowsPerSplat = shDegree === 1 ? 3 : shDegree === 2 ? 9 : shDegree >= 3 ? 12 : 0;
    if (this.hasSH) {
      const shHeight = this.height * this.shRowsPerSplat;
      this.shData = new Uint16Array(this.width * shHeight * 4);
      this.shTexture = new THREE.DataTexture(
        this.shData, this.width, shHeight, THREE.RGBAFormat, THREE.HalfFloatType
      );
      this.shTexture.needsUpdate = true;
    } else {
      this.shData = null;
      this.shTexture = null;
    }

    // Exact allocator state.
    //  _liveBlocks    = Map offset -> count, one entry per currently-
    //                   allocated block (exact size, no rounding).
    //  _freeIntervals = array of {offset, count}, sorted by offset, always
    //                   coalesced (no two entries are ever adjacent).
    this._liveBlocks = new Map();
    this._freeIntervals = this.capacity > 0 ? [{ offset: 0, count: this.capacity }] : [];
    this.residentCount = 0; // sum of live (exact) counts

    // Same quarantine rationale as SplatPool — see its own comment: the sort
    // worker's "this slot is live" state and the GPU texture at that slot
    // update over separate async channels, so a just-freed block needs to
    // wait a few ticks before it's handed to a new chunk.
    this._quarantine = []; // { offset, count, ticksLeft, needSort }
    this.sortsPosted = 0; // see splatPool.js quarantineDone
    this.sortsLanded = 0;
  }

  /** Exact-sized first-fit allocation. Returns the offset, or -1 if no free
   * interval is big enough (SplatOctree then keeps the leaf at its current
   * LOD, same as the buddy allocator's failure path — no repack fallback
   * here either; SplatOctree's own defrag mechanism, when enabled, is what
   * reactively closes gaps after a failure like this). */
  alloc(count) {
    if (count <= 0) return -1;
    const intervals = this._freeIntervals;
    for (let i = 0; i < intervals.length; i++) {
      const iv = intervals[i];
      if (iv.count < count) continue;
      const offset = iv.offset;
      if (iv.count === count) {
        intervals.splice(i, 1);
      } else {
        iv.offset += count;
        iv.count -= count;
      }
      this._liveBlocks.set(offset, count);
      this.residentCount += count;
      return offset;
    }
    return -1;
  }

  /** One past the highest slot that may hold splats (live or quarantined):
   * the start of the free interval reaching the end of the pool, if any.
   * First-fit keeps allocations clustered low, so this is usually well
   * under capacity — WebGPU's GPU sort only dispatches this far. */
  get usedTop() {
    const iv = this._freeIntervals;
    const last = iv.length ? iv[iv.length - 1] : null;
    return last && last.offset + last.count === this.capacity ? last.offset : this.capacity;
  }

  /** Advance the freed-block quarantine by one frame. Call once per frame
   * (SplatOctree.update) — same contract as SplatPool.tick(). */
  tick() {
    if (this._quarantine.length === 0) return;
    const keep = [];
    for (const q of this._quarantine) {
      q.ticksLeft--;
      if (!quarantineDone(this, q)) { keep.push(q); continue; }
      this._release(q.offset, q.count);
    }
    this._quarantine = keep;
  }

  // Binary-search insert into the offset-sorted _freeIntervals, coalescing
  // with the immediately-adjacent interval(s) on either side if present —
  // the exact-allocator equivalent of the buddy allocator's XOR-buddy merge,
  // generalized to arbitrary (not just power-of-two-aligned) sizes.
  _release(offset, count) {
    const intervals = this._freeIntervals;
    let lo = 0, hi = intervals.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (intervals[mid].offset < offset) lo = mid + 1; else hi = mid;
    }
    // lo = insertion index (first interval with offset >= this offset)
    const prev = lo > 0 ? intervals[lo - 1] : null;
    const next = lo < intervals.length ? intervals[lo] : null;
    const mergeLeft = !!(prev && prev.offset + prev.count === offset);
    const mergeRight = !!(next && offset + count === next.offset);
    if (mergeLeft && mergeRight) {
      prev.count += count + next.count;
      intervals.splice(lo, 1);
    } else if (mergeLeft) {
      prev.count += count;
    } else if (mergeRight) {
      next.offset = offset;
      next.count += count;
    } else {
      intervals.splice(lo, 0, { offset, count });
    }
  }

  /** Returns a previously-alloc'd block to the pool (after quarantine).
   * Same contract as SplatPool.free() — `count` must match what was
   * allocated (the exact allocator doesn't look the size up the way the
   * buddy allocator's order-map does, so a mismatched count would corrupt
   * free-list accounting; every caller in this codebase already tracks its
   * own offset/count pair, e.g. SplatOctree.resident[i].offset/.count). */
  free(offset, count) {
    if (!this._liveBlocks.has(offset)) return; // double-free / not ours — ignore
    this._liveBlocks.delete(offset);
    this.residentCount = Math.max(0, this.residentCount - count);
    this._zeroSlotsWebGPU(offset, count);
    this.version++;
    this._quarantine.push({ offset, count, ticksLeft: QUARANTINE_TICKS, needSort: this.sortsPosted + 1 });
  }

  /** Invariant check — call from the console via
   * window.__splatOctree.pool._audit(). Same shape/contract as SplatPool's:
   * collects every live / free / quarantined block, sorts by offset,
   * verifies no overlap and full [0, capacity) tiling. Optionally pass the
   * octree's `resident` array (window.__splatOctree.pool._audit(window.__splatOctree.resident))
   * to ALSO cross-check that every resident leaf's {offset, count} matches a
   * live block exactly — a check the buddy allocator's _audit() never needed
   * (normal LOD-swap code keeps res.offset in sync by construction) but is
   * specifically useful here to catch a defrag slide that forgot to update
   * res.offset. */
  _audit(residentForCrossCheck) {
    const blocks = [];
    for (const [off, count] of this._liveBlocks) blocks.push([off, count, 'live']);
    for (const iv of this._freeIntervals) blocks.push([iv.offset, iv.count, 'free']);
    for (const q of this._quarantine) blocks.push([q.offset, q.count, 'quar']);
    blocks.sort((a, b) => a[0] - b[0]);
    let cursor = 0;
    const problems = [];
    for (const [off, size, kind] of blocks) {
      if (off < cursor) problems.push(`overlap: ${kind} block at ${off} (size ${size}) starts before cursor ${cursor}`);
      else if (off > cursor) problems.push(`gap: ${off - cursor} unaccounted slots at ${cursor}`);
      cursor = Math.max(cursor, off + size);
    }
    if (cursor < this.capacity) problems.push(`tail gap: ${this.capacity - cursor} slots after ${cursor}`);
    if (cursor > this.capacity) problems.push(`overrun: cursor ${cursor} > capacity ${this.capacity}`);
    if (residentForCrossCheck) {
      for (let i = 0; i < residentForCrossCheck.length; i++) {
        const res = residentForCrossCheck[i];
        if (res.level < 0) continue;
        const liveCount = this._liveBlocks.get(res.offset);
        if (liveCount === undefined) problems.push(`resident leaf ${i} claims offset ${res.offset} but no live block there`);
        else if (liveCount !== res.count) problems.push(`resident leaf ${i} claims count ${res.count} at offset ${res.offset} but live block has ${liveCount}`);
      }
    }
    const ok = problems.length === 0;
    if (!ok) console.error('[SplatPoolExact._audit] FAIL', problems);
    return { ok, blocks: blocks.length, live: this._liveBlocks.size, residentCount: this.residentCount, problems };
  }

  /** Same contract/shape as SplatPool.freeStats() — now exact-accurate
   * (no power-of-two skew): totalFree is the true free splat count,
   * largestFreeBlock the true largest contiguous run. */
  freeStats() {
    let totalFree = 0, largest = 0;
    for (const iv of this._freeIntervals) {
      totalFree += iv.count;
      if (iv.count > largest) largest = iv.count;
    }
    return { totalFree, largestFreeBlock: largest };
  }

  // ─── GPU upload — identical to SplatPool's; offset-to-texel mapping
  // doesn't depend on how offsets were allocated. ────────────────────────
  beginWrites() { this._writeDepth = (this._writeDepth || 0) + 1; }
  endWrites() {
    this._writeDepth = Math.max(0, (this._writeDepth || 0) - 1);
    if (this._writeDepth === 0 && this._dirtyWrites && !this.isWebGPU) { this.gl.resetState(); this._dirtyWrites = false; }
  }

  write(offset, count, chunk) {
    this.version++;
    if (this.isWebGPU) {
      this._writeTextureWebGPU(this.centerAndScaleTexture, chunk.centerAndScale, offset, count);
      this._writeTextureWebGPU(this.covAndColorTexture, chunk.covAndColor, offset, count);
      if (this.hasSH && this.shTexture && chunk.sh) this._writeShTextureWebGPU(chunk.sh, offset, count);
      return;
    }
    const context = this.gl.getContext();
    this._writeTexture(context, this.centerAndScaleTexture, chunk.centerAndScale, context.RGBA, context.FLOAT, offset, count, 4);
    this._writeTexture(context, this.covAndColorTexture, chunk.covAndColor, context.RGBA_INTEGER, context.UNSIGNED_INT, offset, count, 4);
    if (this.hasSH && this.shTexture && chunk.sh) {
      this._writeShTexture(context, chunk.sh, offset, count);
    }
    this._dirtyWrites = true;
    if (!this._writeDepth) { this.gl.resetState(); this._dirtyWrites = false; } // not inside a begin/end batch
  }

  _writeTexture(context, tex, data, fmt, type, offset, count, comps) {
    const props = this.gl.properties.get(tex);
    context.bindTexture(context.TEXTURE_2D, props.__webglTexture);
    let remaining = count;
    let pos = offset;
    let srcOff = 0;
    while (remaining > 0) {
      const xoffset = pos % this.width;
      const yoffset = Math.floor(pos / this.width);
      const rowSpan = Math.min(this.width - xoffset, remaining);
      context.texSubImage2D(context.TEXTURE_2D, 0, xoffset, yoffset, rowSpan, 1, fmt, type, data, srcOff * comps);
      pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
    }
  }

  _writeShTexture(context, shData, offset, count) {
    const props = this.gl.properties.get(this.shTexture);
    context.bindTexture(context.TEXTURE_2D, props.__webglTexture);
    const rows = this.shRowsPerSplat;

    const rowMajor = new Uint16Array(count * rows * 4);
    for (let i = 0; i < count; i++) {
      for (let r = 0; r < rows; r++) {
        const src = (i * rows + r) * 4;
        const dst = (r * count + i) * 4;
        rowMajor[dst] = shData[src];
        rowMajor[dst + 1] = shData[src + 1];
        rowMajor[dst + 2] = shData[src + 2];
        rowMajor[dst + 3] = shData[src + 3];
      }
    }

    for (let r = 0; r < rows; r++) {
      const rowSlice = rowMajor.subarray(r * count * 4, (r + 1) * count * 4);
      let remaining = count, pos = offset, srcOff = 0;
      while (remaining > 0) {
        const xoffset = pos % this.width;
        const yoffset = Math.floor(pos / this.width) * rows + r;
        const rowSpan = Math.min(this.width - xoffset, remaining);
        context.texSubImage2D(context.TEXTURE_2D, 0, xoffset, yoffset, rowSpan, 1, context.RGBA, context.HALF_FLOAT, rowSlice, srcOff * 4);
        pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
      }
    }
  }

  // WebGPU twins of _writeTexture/_writeShTexture: same row-span walk, one
  // GPUQueue.writeTexture per destination row span.
  _writeTextureWebGPU(tex, data, offset, count) {
    let remaining = count, pos = offset, srcOff = 0;
    while (remaining > 0) {
      const xoffset = pos % this.width;
      const yoffset = Math.floor(pos / this.width);
      const rowSpan = Math.min(this.width - xoffset, remaining);
      writeTextureRowWebGPU(this.gl, tex, xoffset, yoffset, rowSpan, data.subarray(srcOff * 4, (srcOff + rowSpan) * 4), BYTES_PER_TEXEL.rgba32);
      pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
    }
  }

  _writeShTextureWebGPU(shData, offset, count) {
    const rows = this.shRowsPerSplat;
    for (let r = 0; r < rows; r++) {
      // Gather row r of each splat (source is splat-major, see _writeShTexture).
      let remaining = count, pos = offset, srcOff = 0;
      while (remaining > 0) {
        const xoffset = pos % this.width;
        const yoffset = Math.floor(pos / this.width) * rows + r;
        const rowSpan = Math.min(this.width - xoffset, remaining);
        const out = new Uint16Array(rowSpan * 4);
        for (let i = 0; i < rowSpan; i++) {
          const src = ((srcOff + i) * rows + r) * 4;
          out[i * 4] = shData[src]; out[i * 4 + 1] = shData[src + 1];
          out[i * 4 + 2] = shData[src + 2]; out[i * 4 + 3] = shData[src + 3];
        }
        writeTextureRowWebGPU(this.gl, this.shTexture, xoffset, yoffset, rowSpan, out, BYTES_PER_TEXEL.rgba16);
        pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
      }
    }
  }

  // WebGPU: freed slots must become invisible to the GPU sort (it has no
  // live-slot list, unlike the WebGL CPU sort worker) — zero their
  // covAndColor texels (alpha 0 → skipped).
  _zeroSlotsWebGPU(offset, count) {
    if (!this.isWebGPU) return;
    zeroRangeWebGPU(this.gl, this.covAndColorTexture, this.width, offset, count, BYTES_PER_TEXEL.rgba32);
  }

  // WebGPU: create the GPU textures now, then drop the CPU seed arrays
  // (the WebGL path does this from connect() once its textures exist).
  initGpuTextures() {
    if (!this.isWebGPU || this._gpuTexturesReady) return;
    initPoolTexturesWebGPU(this.gl, [this.centerAndScaleTexture, this.covAndColorTexture, this.shTexture]);
    this._gpuTexturesReady = true;
    this.releaseInitBuffers();
  }

  releaseInitBuffers() {
    if (this._initBuffersReleased) return;
    this._initBuffersReleased = true;
    for (const tex of [this.centerAndScaleTexture, this.covAndColorTexture, this.shTexture]) {
      if (!tex) continue;
      if (tex.image) tex.image.data = null;
      if (tex.source && tex.source.data) tex.source.data = null;
    }
    this.centerAndScaleData = null;
    this.covAndColorData = null;
    this.shData = null;
  }

  destroy() {
    this.centerAndScaleTexture.dispose();
    this.covAndColorTexture.dispose();
    this.shTexture?.dispose();
  }
}
